import { buildProps, definePropType } from '@ll-plus/utils'

import type { ExtractPropTypes } from 'vue'

import type { TreeProps as AntTreePrps } from 'ant-design-vue'

import { FieldNames, Key } from 'ant-design-vue/es/vc-tree/interface'
import { IconProps } from '../../icon'
import type Tree from './tree.vue'

export const treeProps = buildProps({
  selectedKeys: {
    type: definePropType<Key[]>(Array),
  },
  expandedKeys: {
    type: definePropType<Key[]>(Array),
  },
  checkedKeys: {
    type: definePropType<AntTreePrps['checkedKeys']>([Array, Object]),
  },
  treeData: {
    type: definePropType<AntTreePrps['treeData']>(Array),
    default: () => [] as AntTreePrps['treeData'],
  },
  // 默认展开所有树节点
  defaultExpandAll: {
    type: Boolean,
    default: false,
  },
  // 映射字段
  fieldNames: {
    type: definePropType<FieldNames>(Object),
    default: () =>
      ({
        children: 'children',
        title: 'title',
        key: 'key',
      } as FieldNames),
  },
  // 支持多选
  multiple: {
    type: Boolean,
    default: false,
  },
  // 父子节点选中状态不再关联
  checkStrictly: {
    type: Boolean,
    default: false,
  },
  // 展开的图标
  expandIcon: {
    type: definePropType<IconProps>(Object),
  },
  // 收起的图标
  collapseIcon: {
    type: definePropType<IconProps>(Object),
  },
  // 虚拟滚动
  virtual: {
    type: Boolean,
    default: true,
  },
  // 组件高度
  height: {
    type: Number,
  },
  // 显示标题前图标
  showIcon: {
    type: Boolean,
    default: false,
  },
  // 点击行展开
  clickRowExpand: {
    type: Boolean,
    default: false,
  },
  // 开启操作按钮
  enableOperate: {
    type: Boolean,
    default: false,
  },
  // 启用异步加载
  enableLazyLoad: {
    type: Boolean,
    default: false,
  },
  // 异步加载函数
  loadData: {
    type: definePropType<AntTreePrps['loadData']>(Function),
  },
  // 缓存已加载数据
  cacheLoadedData: {
    type: Boolean,
    default: true,
  },
  // 已经加载的节点
  loadedKeys: {
    type: definePropType<Key[]>(Array),
  },

  // 连接线
  showLine: {
    type: Boolean,
    default: false,
  },

  // 显示叶子图标
  showLeafIcon: {
    type: Boolean,
    default: false,
  },

  // 文本颜色
  textColor: {
    type: String,
    default: 'var(--color-text)',
  },

  // 文本大小
  textSize: {
    type: Number,
    default: 14,
  },

  // 图标大小
  iconSize: Number,

  // 图标颜色
  iconColor: String,
} as const)

export const treeEmits = {
  'update:selectedKeys': (value: any) => !!value,
  'update:expandedKeys': (value: any) => !!value,
  'update:checkedKeys': (value: any) => !!value,
  expand: (expandedKeys: Key[], e?: any) => !!expandedKeys || !!e,
  select: (selectKeys: Key[], e?: any) => !!selectKeys || !!e,
  check: (checkKeys: AntTreePrps['checkedKeys'], e?: any) => !!checkKeys || !!e,
  nodeClick: (nodeData: any) => !!nodeData,
} as const

// props
export type TreeProps = ExtractPropTypes<typeof treeProps>

// emits
export type TreeEmits = typeof treeEmits

// instance
export type TreeInstance = InstanceType<typeof Tree>
